import { ChevronDown, Plus, UploadCloud, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useState } from 'react';

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderCode?: string;
}

export default function OrderDetailsModal({
  isOpen,
  onClose,
  orderCode = '00-1027-69289',
}: OrderDetailsModalProps) {
  const [relatedItemsEnabled, setRelatedItemsEnabled] = useState(true);
  const [relatedOrdersEnabled, setRelatedOrdersEnabled] = useState(true);

  if (!isOpen) return null;

  const Field = ({ label, value = '', span = 1 }: { label: string; value?: string; span?: number }) => (
    <div className={span === 2 ? 'col-span-2' : span === 3 ? 'col-span-3' : ''}>
      <label className="mb-1 block text-[12px] font-medium text-[#2F3655]">{label}</label>
      <input
        type="text"
        readOnly
        defaultValue={value}
        className="h-10 w-full rounded-md border border-[#D8DEEA] bg-white px-3 text-[12px] text-[#586183]"
      />
    </div>
  );

  const SectionHeader = ({ title }: { title: string }) => (
    <div className="mb-4 flex items-center justify-between rounded-md bg-[#ECEEF4] px-4 py-2">
      <h3 className="text-[28px] font-semibold leading-none text-[#24304F]">{title}</h3>
      <button className="inline-flex h-5 w-5 items-center justify-center rounded-[2px] bg-black text-white">
        <Plus size={13} />
      </button>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />

      {/* Drawer-style panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-[1220px] bg-[#F4F5F8] shadow-2xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between bg-secondary-500 px-6 py-4 text-white">
            <div className="flex items-center gap-4">
              <h2 className="text-[36px] font-semibold leading-none">Order Details</h2>
              <span className="rounded-full bg-white/15 px-3 py-1 text-[12px] font-medium">
                #{orderCode}
              </span>
            </div>
            <button
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#426198] text-white hover:bg-[#3A588C]"
            >
              <X size={24} />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {/* Top summary */}
            <div className="mb-5 flex items-center gap-6 rounded-xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
              <img
                src="/img/Rectangle 1419.png"
                alt="Diamond Ring"
                className="h-[132px] w-[132px] rounded-[24px] object-cover"
              />
              <div className="flex h-[132px] flex-col justify-center">
                <h3 className="text-[30px] font-semibold leading-none text-[#111827]">Diamond Ring</h3>
                <div className="mt-3 space-y-2 text-[14px] text-[#141B31]">
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={relatedItemsEnabled}
                        onChange={() => setRelatedItemsEnabled((prev) => !prev)}
                      />
                      <div className="relative h-5 w-10 rounded-full bg-[#78829D] transition-colors peer-checked:bg-[#1E3255] after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-5" />
                    </label>
                    <span>Related Items to this others</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={relatedOrdersEnabled}
                        onChange={() => setRelatedOrdersEnabled((prev) => !prev)}
                      />
                      <div className="relative h-5 w-10 rounded-full bg-[#78829D] transition-colors peer-checked:bg-[#1E3255] after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-5" />
                    </label>
                    <span>Related Orders to this Customers</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Marketplace Information */}
            <div className="mb-5 rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
              <div className="mb-4 flex items-center justify-between rounded-md bg-[#ECEEF4] px-4 py-2">
                <h3 className="text-[28px] font-semibold leading-none text-[#24304F]">Marketplace Information</h3>
                <ChevronDown size={16} className="text-[#2F3655]" />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Field label="Order Date & Time" value="2024-07-31 10.25.20" />
                <Field label="Customer Language" value="EN" />
                <Field label="Marketplace" value="Gulf Market" />

                <Field label="Marketplace Name" value="2024-07-31 10.25.20" />
                <Field label="MKT Product Name" value="Diamond Ring" />
                <Field label="Ordered Qty" value="1" />

                <Field label="Marketplace Url" value="Open URL" />
                <Field label="Manual Order/Platform Order" value="Platform Order" />
                <Field label="Product Price" value="77.12" />

                <Field label="Shipping Fee" value="3" />
                <Field label="Taxes" value="3" />
                <Field label="Mahalkum Fees" value="0.0000" />

                <Field label="MKT Taxes" />
                <Field label="MKT Order Fees" />
                <Field label="Order Completion" />

                <Field label="MKT Weight" value="0" />
                <Field label="Customer Selected Category" />
                <Field label="Shipping Method" />
              </div>
            </div>

            <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
                <SectionHeader title="Main Order Payment" />
                <div className="mb-4 grid grid-cols-3 border-b border-[#DFE4EE] pb-2 text-[12px] text-[#2F3655]">
                  <span>Initial Methods</span>
                  <span>Status</span>
                  <span>Transaction ID</span>
                </div>
                <div className="mb-5 grid grid-cols-3 text-[12px] text-[#5E6784]">
                  <span>E - Wallet</span>
                  <span>N/A</span>
                  <span>¥298</span>
                </div>
                <div className="flex gap-2">
                  <button className="h-10 flex-1 rounded-md bg-[#1A1F2D] text-[12px] font-semibold text-white">Pending</button>
                  <button className="h-10 flex-1 rounded-md border border-[#D2D8E4] bg-white text-[12px] font-semibold text-[#2F3655]">Verified</button>
                  <button className="h-10 flex-1 rounded-md bg-[#F5C500] text-[12px] font-semibold text-[#1F2937]">Submit</button>
                </div>
              </div>

              <div className="rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
                <SectionHeader title="Order Completion" />
                <div className="grid grid-cols-2 gap-2">
                  <button className="h-11 rounded-md border border-[#D2D8E4] bg-white text-[12px] font-semibold text-[#2F3655]">Not Required</button>
                  <button className="h-11 rounded-md border border-[#D2D8E4] bg-white text-[12px] font-semibold text-[#2F3655]">Required</button>
                </div>
              </div>
            </div>

            <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
                <SectionHeader title="Customer External Notes" />
                <label className="mb-2 block text-[12px] font-medium text-[#2F3655]">External Notes</label>
                <textarea className="h-[72px] w-full resize-none rounded-md border border-[#D8DEEA] bg-white px-3 py-2 text-[12px] text-[#586183]" />
              </div>

              <div className="rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
                <SectionHeader title="Upload Images" />
                <button className="flex h-[78px] w-full items-center justify-center gap-2 rounded-md border border-dashed border-[#E7CE70] bg-[#FFFBEA] text-[14px] font-medium text-[#2F3655]">
                  <UploadCloud size={18} className="text-[#E0B100]" />
                  Upload Images
                </button>
              </div>
            </div>

            <div className="mb-5 rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
              <SectionHeader title="Procurement" />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Field label="Marketplace Order Status" value="Platform Order" />
                <Field label="Status Reason" value="77.12" />
                <Field label="Marketplace Order No." />

                <Field label="Marketplace Order Date" />
                <Field label="Ordered Quantity" />
                <Field label="Item Identifier" value="AJTL" />

                <Field label="Item Identifier 2" value="HDWEEA" span={2} />
                <Field label="Procurement Issues Notes" value="Select" />
              </div>
            </div>

            <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
              <button className="h-12 rounded-md border border-[#C9D1DF] bg-white text-[14px] font-medium text-[#2F3655]">1st Audit</button>
              <button className="h-12 rounded-md border border-[#C9D1DF] bg-white text-[14px] font-medium text-[#2F3655]">2nd Audit</button>
            </div>

            <div className="mb-5 rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
              <SectionHeader title="After Procurement" />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Field label="Marketplace Currency" value="Select" />
                <Field label="Marketplace VAI" value="77.12" />
                <Field label="Marketplace Price VAT included" />

                <Field label="Marketplace Price" />
                <Field label="Marketplace VAT Price" />
                <Field label="MKT Shipping Fees" value="AJTL" />

                <Field label="Marketplace Total Cost" />
                <Field label="Marketplace Carrier" />
                <Field label="MKT Shipping Fees" />

                <Field label="MKT Tracking No" />
                <Field label="Ex. rate" value="1" />
                <Field label="Credit/Debit Card" value="Not Set" />
              </div>
            </div>

            <div className="mb-5 rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
              <SectionHeader title="Market Shipping Status" />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {['On its Way', 'Delayed', 'Undelivered', 'Received'].map((status) => (
                  <button
                    key={status}
                    className="h-12 rounded-md border border-[#C9D1DF] bg-white text-[14px] font-medium text-[#2F3655]"
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.04)]">
              <div className="mb-3 flex items-center justify-between rounded-md bg-[#ECEEF4] px-4 py-2">
                <h3 className="text-[28px] font-semibold leading-none text-[#24304F]">Order Overview Logs</h3>
                <button className="rounded-xl bg-[#F5C500] px-4 py-2 text-[14px] font-semibold text-[#1F2937]">View All</button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-[#DDE2EC] bg-white px-6 py-4">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button className="h-10 min-w-[96px] rounded-md bg-[#1A1F2D] px-4 text-[12px] font-semibold text-white">3021</button>
              <button className="h-10 min-w-[96px] rounded-md bg-[#1A1F2D] px-4 text-[12px] font-semibold text-white">Next</button>
              <button className="h-10 min-w-[128px] rounded-md bg-[#F5C500] px-4 text-[12px] font-semibold text-[#1F2937]">Save Changes</button>
              <button
                onClick={onClose}
                className="h-10 min-w-[96px] rounded-md bg-[#FF2D75] px-4 text-[12px] font-semibold text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

